import { SxProps } from "@mui/material";
import { ParameterGroup, Parameter } from "../../../common/types/parameters";
import {
  BAR_CHART_COLOR_KAPITALKOSTNAD,
  BAR_CHART_COLOR_DRIFT_UNDERHALL,
  BAR_CHART_COLOR_ENERGI,
  BAR_CHART_COLOR_FJARRVARME_EFFEKT,
  BAR_CHART_COLOR_FLODESKOSTNAD,
  BAR_CHART_COLOR_EXTRA_REINVESTERING,
} from "../../../components/data-presentation/barchart.colors";
import BarChart, {
  Orientation,
} from "../../../components/data-presentation/barchart";
import { buildSupplementalHeatingTooltipMirrors } from "../../../components/data-presentation/barchart.supplemental-district-heating";
import ChartLoadingOverlay from "../../../components/data-presentation/chart-loading-overlay";

const costTypes: { id: string[]; name?: string }[] = [
  { id: ["IncreasedGridCost"] },
  { id: ["FlowCost"] },
  { id: ["LoadCost"] },
  { id: ["EnergyCost"] },
  { id: ["MaintenanceCost", "HeatPumpMaintenanceCost"] },
  { id: ["CapitalCost"] },
  { id: ["SupplementalDistrictHeatingTotalCostsSum"] },
];

const DETAIL_DISTRICT_HEATING_COST_SUFFIXES = costTypes
  .slice(0, 6)
  .map((ct) => ct.id[0]);

function getExpectedParameterId(
  costTypeId: string,
  heatingTechnologyId: string
): string {
  return `${heatingTechnologyId}${costTypeId}`;
}

function getParametersOfCostType(
  costTypeIds: string[],
  parameters: Parameter[],
  heatingTechnology: { id: string; title: string }
): Parameter[] {
  return costTypeIds
    .map((id) => getExpectedParameterId(id, heatingTechnology.id))
    .map((paramId) => parameters.find((parameter) => parameter.id === paramId))
    .filter((parameter) => parameter !== undefined) as Parameter[];
}

function resolveCostTypeDisplayNames(
  parameters: Parameter[],
  heatingTechnologies: { id: string; title: string }[]
): (string | undefined)[] {
  return costTypes.map(
    (costType) =>
      heatingTechnologies
        .flatMap((heatingTechnology) =>
          getParametersOfCostType(costType.id, parameters, heatingTechnology)
        )
        .find((param) => param.displayName)?.displayName
  );
}

function convertToParameterGroups(
  parameters: Parameter[],
  heatingTechnologies: { id: string; title: string; hidden?: boolean }[]
): ParameterGroup[] {
  const costTypeDisplayNames = resolveCostTypeDisplayNames(
    parameters,
    heatingTechnologies
  );
  let parameterGroups = heatingTechnologies.map(
    (heatingTechnology) =>
      ({
        id: heatingTechnology.id,
        displayName: heatingTechnology.title,
        description: "",
        hidden: heatingTechnology.hidden,
        parameters: costTypes.map(
          (costType, costTypeIndex) =>
            getParametersOfCostType(
              costType.id,
              parameters,
              heatingTechnology
            ).at(0) ??
            ({
              id: getExpectedParameterId(costType.id[0], heatingTechnology.id),
              value: "0",
              displayName: costTypeDisplayNames[costTypeIndex],
            } as Parameter)
        ),
      }) as ParameterGroup
  );

  return parameterGroups;
}

function getDistrictHeatingRowLabelsForMirror(
  parameterGroups: ParameterGroup[],
  parameters: Parameter[]
): string[] {
  const dhGroup = parameterGroups.find((g) => g.id === "DistrictHeating");
  return costTypes.map((ct, i) => {
    const labelFromDh = dhGroup?.parameters[i]?.displayName?.trim();
    if (labelFromDh) {
      return labelFromDh;
    }
    const fromDhParam = parameters
      .find((p) => p.id === getExpectedParameterId(ct.id[0], "DistrictHeating"))
      ?.displayName?.trim();
    if (fromDhParam) {
      return fromDhParam;
    }
    return ct.id[0];
  });
}

export default function DetailsChart(params: {
  heatingTechnologies: { id: string; title: string; hidden?: boolean }[];
  parameters: Parameter[];
  sx?: SxProps;
  displayLegends?: boolean;
  includeVat: boolean;
  toPrint: boolean;
  isCalculating?: boolean;
}) {
  const chartParameterGroups = convertToParameterGroups(
    params.parameters,
    params.heatingTechnologies
  );
  const dhLabels = getDistrictHeatingRowLabelsForMirror(
    chartParameterGroups,
    params.parameters
  );
  const supplementalDistrictHeatingTooltipByCategory =
    buildSupplementalHeatingTooltipMirrors(
      params.parameters,
      params.heatingTechnologies,
      dhLabels,
      [...DETAIL_DISTRICT_HEATING_COST_SUFFIXES],
      [
        BAR_CHART_COLOR_KAPITALKOSTNAD,
        BAR_CHART_COLOR_DRIFT_UNDERHALL,
        BAR_CHART_COLOR_ENERGI,
        BAR_CHART_COLOR_FJARRVARME_EFFEKT,
        BAR_CHART_COLOR_FLODESKOSTNAD,
        BAR_CHART_COLOR_EXTRA_REINVESTERING,
      ]
    );

  return (
    <ChartLoadingOverlay
      loading={
        !params.toPrint &&
        (params.parameters.length === 0 || !!params.isCalculating)
      }
      sx={params.sx}
    >
      {params.parameters.length > 0 && (
        <BarChart
          description="Kostnader per år"
          parameterGroups={chartParameterGroups}
          unit="Kronor per år"
          orientation={Orientation.horizontal}
          displayLegends={params.displayLegends}
          includeVat={params.includeVat}
          toPrint={params.toPrint}
          supplementalDistrictHeatingTooltipByCategory={
            supplementalDistrictHeatingTooltipByCategory
          }
        />
      )}
    </ChartLoadingOverlay>
  );
}
